import {useEffect, useState} from 'react'
import {Menu, MenuButton, MenuItem, MenuItems} from '@headlessui/react'
import {ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon} from '@heroicons/react/16/solid'
import {Button} from '@/components/tailwind/button'
import {Input} from '@/components/tailwind/input'
import {useTranslation} from '@/i18n/hooks'
import type {Namespace} from '@/i18n/types'

export type PeriodType = 'fullAccount' | 'year' | 'quarter' | 'month' | 'custom';

export interface DateRange {
    startDate: string;
    endDate: string;
}

interface PeriodStepperFilterProps {
    onChange: (range: DateRange | null) => void;
    translationNamespace?: Namespace;
    translationKeyPrefix?: string;
    includeFullAccount?: boolean;
    includeCustom?: boolean;
    defaultType?: PeriodType;
}

// Local calendar date; toISOString would shift the day across time zones.
function formatDate(date: Date): string {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

function yearRange(year: number): DateRange {
    return {startDate: `${year}-01-01`, endDate: `${year}-12-31`}
}

function quarterRange(year: number, quarter: number): DateRange {
    const startMonth = (quarter - 1) * 3
    return {
        startDate: formatDate(new Date(year, startMonth, 1)),
        endDate: formatDate(new Date(year, startMonth + 3, 0)),
    }
}

function monthRange(year: number, month: number): DateRange {
    return {
        startDate: formatDate(new Date(year, month - 1, 1)),
        endDate: formatDate(new Date(year, month, 0)),
    }
}

export function PeriodStepperFilter({
    onChange,
    translationNamespace = 'reports',
    translationKeyPrefix = 'statistics.timePeriod',
    includeFullAccount = false,
    includeCustom = true,
    defaultType = 'year',
}: PeriodStepperFilterProps) {
    const {t, i18n} = useTranslation(translationNamespace)
    const key = (suffix: string) => `${translationKeyPrefix}.${suffix}`
    const now = new Date()

    const [type, setType] = useState<PeriodType>(defaultType)
    const [year, setYear] = useState(now.getFullYear())
    const [quarter, setQuarter] = useState(Math.floor(now.getMonth() / 3) + 1)
    const [month, setMonth] = useState(now.getMonth() + 1)
    const [customStart, setCustomStart] = useState('')
    const [customEnd, setCustomEnd] = useState('')

    useEffect(() => {
        let range: DateRange | null

        switch (type) {
            case 'fullAccount':
                range = null
                break
            case 'year':
                range = yearRange(year)
                break
            case 'quarter':
                range = quarterRange(year, quarter)
                break
            case 'month':
                range = monthRange(year, month)
                break
            case 'custom':
                if (customStart && customEnd) {
                    range = {startDate: customStart, endDate: customEnd}
                } else {
                    return
                }
                break
        }

        onChange(range)
    }, [type, year, quarter, month, customStart, customEnd, onChange])

    const types: PeriodType[] = [
        ...(includeFullAccount ? ['fullAccount' as const] : []),
        'year', 'quarter', 'month',
        ...(includeCustom ? ['custom' as const] : []),
    ]

    function monthName(value: number): string {
        return new Date(2000, value - 1, 1).toLocaleDateString(i18n.language, {month: 'long'})
    }

    function stepPeriod(direction: -1 | 1) {
        if (type === 'year') {
            setYear(year + direction)
        } else if (type === 'quarter') {
            const next = quarter + direction
            if (next < 1) {
                setQuarter(4)
                setYear(year - 1)
            } else if (next > 4) {
                setQuarter(1)
                setYear(year + 1)
            } else {
                setQuarter(next)
            }
        } else if (type === 'month') {
            const next = month + direction
            if (next < 1) {
                setMonth(12)
                setYear(year - 1)
            } else if (next > 12) {
                setMonth(1)
                setYear(year + 1)
            } else {
                setMonth(next)
            }
        }
    }

    function stepperLabel(): string {
        if (type === 'year') return `${year}`
        if (type === 'quarter') return `Q${quarter} ${year}`
        return `${monthName(month)} ${year}`
    }

    const showStepper = type === 'year' || type === 'quarter' || type === 'month'

    return (
        <div className="flex flex-wrap items-center gap-2">
            <Menu as="div" className="relative">
                <MenuButton className="inline-flex justify-center items-center px-3 py-2 text-sm font-medium rounded-md border shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 ease-in-out bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700">
                    {t(key(`presets.${type}`))}
                    <ChevronDownIcon className="ml-2 h-4 w-4" />
                </MenuButton>
                <MenuItems className="absolute left-0 z-10 mt-2 w-40 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 dark:ring-gray-700">
                    {types.map((value) => (
                        <MenuItem key={value}>
                            {({active}) => (
                                <button
                                    onClick={() => setType(value)}
                                    className={`block w-full px-4 py-2 text-left text-sm ${
                                        active
                                            ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                                            : 'text-gray-700 dark:text-gray-300'
                                    }`}
                                >
                                    {t(key(`presets.${value}`))}
                                </button>
                            )}
                        </MenuItem>
                    ))}
                </MenuItems>
            </Menu>

            {showStepper && (
                <div className="flex items-center gap-2">
                    <Button outline onClick={() => stepPeriod(-1)} aria-label={t(key('prev'))}>
                        <ChevronLeftIcon className="h-4 w-4" />
                    </Button>
                    <div className="min-w-[140px] text-center font-medium text-gray-900 dark:text-gray-100">
                        {stepperLabel()}
                    </div>
                    <Button outline onClick={() => stepPeriod(1)} aria-label={t(key('next'))}>
                        <ChevronRightIcon className="h-4 w-4" />
                    </Button>
                </div>
            )}

            {type === 'custom' && (
                <div className="flex items-center gap-2">
                    <Input
                        type="date"
                        aria-label={t(key('startDate'))}
                        value={customStart}
                        onChange={(e) => setCustomStart(e.target.value)}
                    />
                    <span className="text-gray-500">–</span>
                    <Input
                        type="date"
                        aria-label={t(key('endDate'))}
                        value={customEnd}
                        onChange={(e) => setCustomEnd(e.target.value)}
                    />
                </div>
            )}
        </div>
    )
}
